import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { httpClient } from '@/core/api/http-client';
import { useAppStore } from '@/core/store/app.store';
import { toIso3, toIso2 } from '@/core/i18n/country-code';

import type { PropsWithChildren } from 'react';

interface AdminMeResponse {
  allowed_countries: string[]; // ISO2
  country_code: string;       // ISO2
  active_country: string;      // ISO2
  is_owner: boolean;
}

/**
 * Bootstraps the application state by fetching the current user's
 * allowed scope (countries) and ensuring the active country is valid.
 */
export function AuthBootstrap(props: PropsWithChildren) {
  const { isAuthenticated, isLoading: isAuthLoading, getAccessTokenSilently } = useAuth0();
  const { setAllowedCountries, setCountry, country: persistedCountry } = useAppStore();
  const [isBootstrapped, setIsBootstrapped] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || isAuthLoading) return;

    const bootstrap = async () => {
      try {
        const token = await getAccessTokenSilently();
        const iso3 = useAppStore.getState().country;

        const { data: me } = await httpClient.get<AdminMeResponse>('/admin/me', {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Country-Code': toIso2(iso3),
          },
        });

        const allowedIso3 = me.allowed_countries.map(toIso3);
        const defaultIso3 = toIso3(me.country_code);

        setAllowedCountries(allowedIso3);

        // Validate persisted country against allowed list
        const isValid = allowedIso3.includes(persistedCountry);
        if (!isValid) {
          setCountry(defaultIso3);
        }

        setIsBootstrapped(true);
      } catch (error) {
        console.error('Failed to bootstrap auth scope:', error);
        // We still mark as bootstrapped to avoid blocking the app completely,
        // but it will likely fail later on requests.
        setIsBootstrapped(true);
      }
    };

    bootstrap();
  }, [isAuthenticated, isAuthLoading, persistedCountry, setAllowedCountries, setCountry, getAccessTokenSilently]);

  if (isAuthLoading || (isAuthenticated && !isBootstrapped)) {
    return (
      <div className="flex h-screen items-center justify-center bg-base-200">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }

  return <>{props.children}</>;
}
